import React, { useEffect, useMemo, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
  ConnectPayouts,
  ConnectAccountManagement,
} from "@stripe/react-connect-js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Landmark, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * ConnectOnboardModal — renders Stripe Connect Embedded Components (Express)
 * inside our own dashboard. Bank details are tokenised inside Stripe's iframe;
 * nothing sensitive touches our React state or MongoDB.
 *
 * Contract:
 *   - `open` / `onClose`   — Dialog control
 *   - `onExit()`           — called when the parent finishes / exits onboarding
 *                            (parent handler should re-poll /wallet/connect/status
 *                            and refresh the auth user).
 *
 * Requirements at runtime:
 *   - process.env.REACT_APP_STRIPE_PUBLIC_KEY must be a Stripe *publishable*
 *     key (pk_test_… or pk_live_…). We render an inline warning if missing so
 *     the failure mode is clear instead of an obscure Stripe error.
 *   - Backend POST /api/wallet/connect/account-session returns {client_secret}
 *     minted by the mobile server (never persisted here).
 */
export default function ConnectOnboardModal({ open, onClose, onExit }) {
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  const [initError, setInitError] = useState("");
  const [loaderVisible, setLoaderVisible] = useState(true);

  // Build a single Connect instance per modal-open. We tear it down when the
  // dialog closes so Stripe's iframe doesn't linger in the DOM.
  const connectInstance = useMemo(() => {
    if (!open) return null;
    if (!publishableKey) {
      setInitError("Stripe is not configured. Ask an admin to set REACT_APP_STRIPE_PUBLIC_KEY.");
      return null;
    }
    setInitError("");
    setLoaderVisible(true);

    // fetchClientSecret is called by Connect.js both initially AND whenever the
    // embedded UI needs a fresh session. Each call mints a NEW Account Session
    // upstream — we never cache the returned client_secret.
    const fetchClientSecret = async () => {
      try {
        const { data } = await api.post("/wallet/connect/account-session");
        if (!data?.client_secret) throw new Error("No client_secret returned by upstream.");
        return data.client_secret;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = (typeof detail === "object" && detail?.message)
          ? detail.message
          : (formatApiError(e) || "Could not start bank linking.");
        setInitError(msg);
        // Re-throw so Connect.js knows the fetch failed
        throw new Error(msg);
      }
    };

    try {
      return loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret,
        appearance: {
          overlays: "dialog",
          variables: {
            colorPrimary: "#EC4C8A",
            colorText: "#0F172A",
            colorBackground: "#FFFFFF",
            borderRadius: "16px",
            fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
          },
        },
      });
    } catch (e) {
      setInitError(e?.message || "Failed to initialise Stripe Connect.");
      return null;
    }
    // We deliberately re-create the instance on each modal open (open flips
    // false → true) so a fresh Account Session is fetched every session.
  }, [open, publishableKey]);

  // Safety net: ensure loader eventually clears even if Stripe never fires
  // onLoaderStart (defensive against future Connect.js API drift).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setLoaderVisible(false), 8000);
    return () => clearTimeout(t);
  }, [open]);

  const handleExit = () => {
    // Let the parent refresh /wallet/connect/status + /auth/me
    onExit?.();
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-2xl rounded-3xl border-0 p-0 overflow-hidden shadow-pop max-h-[90vh] overflow-y-auto"
        data-testid="connect-onboard-modal"
      >
        <div className="relative bg-gradient-to-br from-brandSlate to-slate-800 px-6 pt-8 pb-6 text-white">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
          <div className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] opacity-90">
            <Landmark className="w-4 h-4" /> Stripe Connect
          </div>
          <DialogHeader className="relative mt-2 text-left space-y-1">
            <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold text-white">
              Link your bank for payouts
            </DialogTitle>
            <DialogDescription className="text-white/90 text-sm leading-relaxed">
              Stripe collects your details securely inside the panel below. We never see or store your sort code or account number.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 bg-white space-y-4" data-testid="connect-onboard-body">
          {initError && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3 flex flex-col gap-2" data-testid="connect-onboard-error">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {initError}
              </div>
              {/* Iteration 60 — offer a redirect-based fallback so parents
                  can still finish linking their bank even if the embedded
                  session mint fails on mobile. */}
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/wallet/connect/onboard", {});
                    if (data?.url) {
                      window.location.href = data.url;
                    } else {
                      setInitError("Stripe onboarding link unavailable. Please try again later.");
                    }
                  } catch (e) {
                    setInitError(formatApiError(e) || "Could not open Stripe onboarding.");
                  }
                }}
                className="self-start text-xs font-bold text-brandPink hover:text-brandPinkDark underline"
                data-testid="connect-onboard-fallback"
              >
                Continue via Stripe onboarding →
              </button>
            </div>
          )}

          {!publishableKey && !initError && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="connect-onboard-no-key">
              Stripe onboarding is not yet configured on this environment.
            </div>
          )}

          {loaderVisible && connectInstance && (
            <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="connect-onboard-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading Stripe onboarding…
            </div>
          )}

          {connectInstance && (
            <ConnectComponentsProvider connectInstance={connectInstance}>
              <div className="rounded-2xl border border-slate-100 overflow-hidden" data-testid="connect-account-onboarding">
                <ConnectAccountOnboarding
                  onLoaderStart={() => setLoaderVisible(true)}
                  onExit={handleExit}
                />
              </div>
              <div className="rounded-2xl border border-slate-100 overflow-hidden" data-testid="connect-payouts-panel">
                <ConnectPayouts onLoaderStart={() => setLoaderVisible(false)} />
              </div>
              <div className="rounded-2xl border border-slate-100 overflow-hidden" data-testid="connect-account-management">
                <ConnectAccountManagement />
              </div>
            </ConnectComponentsProvider>
          )}

          <div className="pt-2 text-[11px] text-slate-500 leading-relaxed flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5" />
            <span>
              Bank details are tokenised inside Stripe&rsquo;s iframe and never touch MyTableTopApp servers or MongoDB.
              Stripe acts as an independent Data Controller for KYC / AML compliance.
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-full text-sm font-semibold text-slate-500 hover:text-brandSlate transition py-1"
            data-testid="connect-onboard-close"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
